import { useState } from 'react';
import { ownerService } from 'services/api';
import Modal from 'components/Modal';
import AvisoModal from 'components/AvisoModal';
import styles from './EditarPropietarioModal.module.css';
import { EMAIL_REGEX } from 'utils/email';

const PHONE_REGEX = /^\d{7,10}$/;
// Mismos topes que valida el backend (ownersController.js · actualizar).
const MAX_NOMBRE = 150;
const MAX_CORREO = 254;

// Con noValidate el navegador no bloquea el envío: se revisa aquí lo mismo que el servidor.
function validarCampos(form) {
  if (!form.nombre.trim()) return 'El nombre es requerido';
  if (form.nombre.trim().length > MAX_NOMBRE || form.apellido.trim().length > MAX_NOMBRE) return `El nombre y el apellido no pueden tener más de ${MAX_NOMBRE} caracteres`;
  if (!form.email.trim()) return 'El correo es requerido';
  if (form.email.trim().length > MAX_CORREO) return `El correo no puede tener más de ${MAX_CORREO} caracteres`;
  if (!EMAIL_REGEX.test(form.email.trim())) return 'El correo no tiene un formato válido';
  if (form.telefono.trim() && !PHONE_REGEX.test(form.telefono.trim())) return 'El teléfono no es válido — debe tener 7 (fijo) o 10 (celular) dígitos, sin espacios ni +57';
  return null;
}

export default function EditarPropietarioModal({ owner, onClose, onDone }) {
  const [form, setForm] = useState({
    nombre: owner.nombre || '', apellido: owner.apellido || '', telefono: owner.telefono || '', email: owner.email || '',
  });
  const [saving, setSaving] = useState(false);
  const [modalError, setModalError] = useState('');
  const set = (campo) => (e) => setForm(f => ({ ...f, [campo]: e.target.value }));
  const correoCambia = form.email.trim().toLowerCase() !== String(owner.email || '').trim().toLowerCase();

  const guardar = async (e) => {
    e.preventDefault();
    const error = validarCampos(form);
    if (error) { setModalError(error); return; }
    setSaving(true);
    setModalError('');
    try {
      const r = await ownerService.update(owner.id, {
        nombre: form.nombre.trim(), apellido: form.apellido.trim(),
        telefono: form.telefono.trim(), email: form.email.trim(),
      });
      onDone(r.data.message);
    } catch (err) {
      // El aviso va dentro del modal: la alerta de la página queda tapada detrás de él.
      setModalError(err.response?.data?.message || 'No se pudo guardar. Inténtalo de nuevo.');
    } finally {
      setSaving(false);
    }
  };

  return (
    <Modal onClose={() => !saving && onClose()} maxWidth={620}>
      <div className="modal-header">
        <h3><i className={`fas fa-pen ${styles.iconoTitulo}`} />Editar propietario</h3>
        <button className="btn-close" onClick={onClose} disabled={saving}>✕</button>
      </div>
      <form onSubmit={guardar} noValidate>
        <div className="modal-body">
          {modalError && (
            <AvisoModal>
              <i className={`fas fa-exclamation-circle ${styles.iconoError}`} />
              {modalError}
            </AvisoModal>
          )}
          <p className={styles.intro}>
            Corrige los datos de la misma persona. Si la unidad se vendió o pasó a otra persona, usa «Cambiar propietario».
          </p>
          <div className={styles.rejilla}>
            <div className="form-group">
              <label className="form-label">Documento</label>
              <input className="form-control" value={`${owner.tipo_identificacion || ''} ${owner.numero_identificacion || ''}`.trim()} disabled readOnly />
              <div className={styles.nota}>El documento no se puede cambiar desde aquí.</div>
            </div>
            <div className="form-group">
              <label className="form-label">Teléfono</label>
              <input className="form-control" value={form.telefono} onChange={set('telefono')} maxLength={10} inputMode="numeric" autoComplete="off" />
            </div>
            <div className="form-group">
              <label className="form-label">Nombre *</label>
              <input className="form-control" value={form.nombre} onChange={set('nombre')} maxLength={MAX_NOMBRE} autoComplete="off" />
            </div>
            <div className="form-group">
              <label className="form-label">Apellido</label>
              <input className="form-control" value={form.apellido} onChange={set('apellido')} maxLength={MAX_NOMBRE} autoComplete="off" />
            </div>
          </div>
          <div className="form-group">
            <label className="form-label">Correo *</label>
            <input type="email" className="form-control" value={form.email} onChange={set('email')} maxLength={MAX_CORREO} autoComplete="off" />
            <div className={styles.nota}>
              Es el correo con el que la persona entra. Si tiene unidades en otro conjunto, solo un super admin lo puede cambiar.
            </div>
            {correoCambia && (
              <div className={styles.aviso}>
                La persona entrará con el correo nuevo. Si ya activó su cuenta, se le avisará en el correo anterior y también en el nuevo.
              </div>
            )}
          </div>
        </div>
        <div className="modal-footer">
          <button type="button" className="btn btn-secondary" onClick={onClose} disabled={saving}>Cancelar</button>
          <button type="submit" className="btn btn-primary" disabled={saving}>
            {saving ? <><div className="spinner" /> Guardando...</> : <><i className="fas fa-check" /> Guardar</>}
          </button>
        </div>
      </form>
    </Modal>
  );
}
